import { useState, useMemo, useRef } from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';

import { Section, SectionCard, SectionHeader } from '@/components/common';
import { RichTextEditor } from '@/components/common/RichTextEditor/RichTextEditor';
import { useUpdateTcRunConfidentialNotes } from '@/queries/useTcRun';
import type { TcRunAppraiseeResponse } from '@/api/types/tcRun.types';
import { useTcRunAppraiseeMutationQueue } from './TcRunAppraiseeMutationQueueContext';

interface ConfidentialNotesSectionProps {
  tcId: number;
  appraisee: TcRunAppraiseeResponse;
  readOnly: boolean;
}

export function ConfidentialNotesSection({ tcId, appraisee, readOnly }: ConfidentialNotesSectionProps) {
  const { runSerialized } = useTcRunAppraiseeMutationQueue();
  const updateMutation = useUpdateTcRunConfidentialNotes();

  const [localNotes, setLocalNotes] = useState<string>(appraisee.confidentialStaffingTcNotes ?? '');
  const [localActiveCoaching, setLocalActiveCoaching] = useState(appraisee.activeCoaching);
  const [localLd, setLocalLd] = useState(appraisee.ldIntervention);
  const [localPip, setLocalPip] = useState(appraisee.pip);
  const [localCheckIn, setLocalCheckIn] = useState(appraisee.tcCheckIn);

  // serverKey/prevKey pattern for cache-aware resets
  const serverKey = useMemo(
    () =>
      JSON.stringify({
        notes: appraisee.confidentialStaffingTcNotes,
        ac: appraisee.activeCoaching,
        ld: appraisee.ldIntervention,
        pip: appraisee.pip,
        ci: appraisee.tcCheckIn,
      }),
    [
      appraisee.confidentialStaffingTcNotes,
      appraisee.activeCoaching,
      appraisee.ldIntervention,
      appraisee.pip,
      appraisee.tcCheckIn,
    ]
  );

  const [prevKey, setPrevKey] = useState(serverKey);
  if (serverKey !== prevKey) {
    setPrevKey(serverKey);
    setLocalNotes(appraisee.confidentialStaffingTcNotes ?? '');
    setLocalActiveCoaching(appraisee.activeCoaching);
    setLocalLd(appraisee.ldIntervention);
    setLocalPip(appraisee.pip);
    setLocalCheckIn(appraisee.tcCheckIn);
  }

  // Guards the window between a save resolving and the `appraisee` prop re-rendering
  // with the server's new value — during which `localNotes !== appraisee.conf…` would
  // otherwise still look "dirty" and re-fire the save with the stale rowVersion.
  const lastSavedNotesRef = useRef<string | null>(null);

  const isNotesDirty = () => {
    const current = localNotes || '';
    if (current === (appraisee.confidentialStaffingTcNotes || '')) return false;
    if (lastSavedNotesRef.current !== null && current === lastSavedNotesRef.current) return false;
    return true;
  };

  // Single endpoint that updates notes + all TC actions together. `override` lets a
  // checkbox handler ship its new value before React's state update has committed.
  // All calls funnel through the page-level queue so they're ordered against any
  // sibling-section mutation on the same appraisal rowVersion.
  const saveCombined = async (
    override?: Partial<{ ac: boolean; ld: boolean; pip: boolean; ci: boolean }>
  ): Promise<void> => {
    const isBlurSave = override === undefined;
    if (isBlurSave && !isNotesDirty()) return;

    const notesToSave = localNotes || null;
    await runSerialized((rowVersion) =>
      updateMutation
        .mutateAsync({
          tcId,
          appraisalId: appraisee.appraisalId,
          data: {
            confidentialStaffingTcNotes: notesToSave,
            activeCoaching: override?.ac ?? localActiveCoaching,
            ldIntervention: override?.ld ?? localLd,
            pip: override?.pip ?? localPip,
            tcCheckIn: override?.ci ?? localCheckIn,
          },
          rowVersion,
        })
        .then(() => {
          lastSavedNotesRef.current = notesToSave ?? '';
        })
    );
  };

  const handleNotesBlur = () => {
    void saveCombined().catch(() => {
      /* 409 surfaced by the global Axios interceptor */
    });
  };

  const makeCheckboxHandler = (
    setter: (v: boolean) => void,
    field: 'ac' | 'ld' | 'pip' | 'ci'
  ) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setter(newValue);
    void saveCombined({ [field]: newValue }).catch(() => {
      /* 409 surfaced globally */
    });
  };

  return (
    <Section>
      <SectionHeader>CONFIDENTIAL NOTES</SectionHeader>
      <SectionCard>
        <RichTextEditor
          content={localNotes}
          onChange={setLocalNotes}
          onBlur={handleNotesBlur}
          placeholder="Confidential staffing TC notes..."
          disabled={readOnly}
          aria-label="Confidential Staffing TC Notes"
        />

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          TC Actions
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
          {/* #1095: PMI Required moved to the Collator's Proposed Actions (Notes to TC). */}
          <FormControlLabel
            control={
              <Checkbox
                checked={localActiveCoaching}
                onChange={makeCheckboxHandler(setLocalActiveCoaching, 'ac')}
                disabled={readOnly}
                inputProps={{ 'aria-label': 'Active Coaching' }}
              />
            }
            label="Active Coaching"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={localLd}
                onChange={makeCheckboxHandler(setLocalLd, 'ld')}
                disabled={readOnly}
                inputProps={{ 'aria-label': 'L&D Intervention' }}
              />
            }
            label="L&D Intervention"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={localPip}
                onChange={makeCheckboxHandler(setLocalPip, 'pip')}
                disabled={readOnly}
                inputProps={{ 'aria-label': 'PIP' }}
              />
            }
            label="PIP"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={localCheckIn}
                onChange={makeCheckboxHandler(setLocalCheckIn, 'ci')}
                disabled={readOnly}
                inputProps={{ 'aria-label': 'Check-in' }}
              />
            }
            label="Check-in"
          />
        </Box>
      </SectionCard>
    </Section>
  );
}
